import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ArrowRightLeft, X, Check, MessageSquare, User as UserIcon } from "@/lib/icons";
import {
  acceptTransfer,
  rejectTransfer,
  getLeadMessages,
} from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const calidadStyles = {
  caliente: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  tibio: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  frio: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
};

export function TransferRequestModal({ open, transfer, currentUser, onClose, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [previewMessages, setPreviewMessages] = useState([]);

  useEffect(() => {
    if (!open || !transfer?.lead?.id) {
      setPreviewMessages([]);
      setShowReject(false);
      setRejectReason("");
      return;
    }
    (async () => {
      const msgs = await getLeadMessages({
        wa_jid: transfer.lead.wa_jid,
        leadId: transfer.lead.id,
        limit: 5,
      });
      setPreviewMessages((msgs || []).slice(-5));
    })();
  }, [open, transfer?.lead?.id, transfer?.lead?.wa_jid]);

  if (!open || !transfer) return null;

  const handleAccept = async () => {
    setLoading(true);
    const { error } = await acceptTransfer(transfer.id, currentUser.id);
    setLoading(false);
    if (error) {
      toast.error(error.message || "No se pudo aceptar la transferencia");
      return;
    }
    toast.success(`${transfer.lead?.nombre_tutor || "Prospecto"} ahora es tuyo`);
    onResolved?.("accepted", transfer);
    onClose();
  };

  const handleReject = async () => {
    setLoading(true);
    const { error } = await rejectTransfer(transfer.id, currentUser.id, rejectReason);
    setLoading(false);
    if (error) {
      toast.error(error.message || "No se pudo rechazar la transferencia");
      return;
    }
    toast.success("Transferencia rechazada");
    onResolved?.("rejected", transfer);
    onClose();
  };

  const lead = transfer.lead || {};
  const fromName = transfer.from_asesor?.nombre || "Una asesora";
  const calidadCls = calidadStyles[lead.calidad_lead] || calidadStyles.tibio;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 text-white flex-shrink-0" style={{ background: "linear-gradient(135deg, #7a1b33, #a3204b)" }}>
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            <h3 className="font-bold text-base">Solicitud de transferencia</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <UserIcon className="h-4 w-4" />
            <span>
              <strong className="text-slate-900 dark:text-slate-100">{fromName}</strong>
              {" "}te quiere transferir un prospecto
            </span>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 space-y-2 border border-slate-200 dark:border-slate-700">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-bold text-slate-900 dark:text-slate-100 text-base">
                  {lead.nombre_tutor || "Prospecto sin nombre"}
                </p>
                {lead.nombre_alumno && (
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                    👶 {lead.nombre_alumno}
                  </p>
                )}
              </div>
              {lead.calidad_lead && (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${calidadCls} capitalize`}>
                  {lead.calidad_lead}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {lead.nivel_educativo && (
                <span className="px-2 py-1 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium">
                  📚 {lead.nivel_educativo}{lead.grado_especifico && ` • ${lead.grado_especifico}`}
                </span>
              )}
              {lead.telefono && (
                <span className="px-2 py-1 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium">
                  📱 {lead.telefono}
                </span>
              )}
            </div>
            {lead.tema_consulta && (
              <p className="text-xs text-slate-600 dark:text-slate-400 pt-1 border-t border-slate-200 dark:border-slate-700 mt-2">
                <span className="font-semibold">Tema:</span> {lead.tema_consulta}
              </p>
            )}
          </div>

          {transfer.motivo && (
            <div className="bg-rose-50 dark:bg-rose-900/20 border-l-4 border-rose-600 rounded-r-lg p-3">
              <p className="text-xs font-semibold text-rose-900 dark:text-rose-300 uppercase tracking-wide">
                Motivo de {fromName}
              </p>
              <p className="text-sm text-rose-800 dark:text-rose-200 mt-1 italic">
                "{transfer.motivo}"
              </p>
            </div>
          )}

          {previewMessages.length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Últimos mensajes
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto bg-slate-50 dark:bg-slate-800/40 rounded-lg p-2 border border-slate-200 dark:border-slate-700">
                {previewMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`text-xs px-2 py-1.5 rounded ${
                      m.direction === "out"
                        ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-200 ml-4"
                        : "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 mr-4"
                    }`}
                  >
                    <span className="line-clamp-2">{m.body || "(sin texto)"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showReject && (
            <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                ¿Por qué rechazas? <span className="text-slate-400">(opcional)</span>
              </p>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej: Estoy saturada, no es mi nivel…"
                rows={2}
                className="resize-none text-sm"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          {!showReject ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowReject(true)}
                disabled={loading}
                className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
              >
                Rechazar
              </Button>
              <Button
                onClick={handleAccept}
                disabled={loading}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white gap-1.5"
              >
                <Check className="h-4 w-4" />
                {loading ? "Aceptando…" : "Aceptar transferencia"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setShowReject(false)} disabled={loading}>
                Volver
              </Button>
              <Button
                onClick={handleReject}
                disabled={loading}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                {loading ? "Rechazando…" : "Confirmar rechazo"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
